import { keyBy, pickBy } from "lodash";
import { CalendarMode, Row } from "PFApp/booking/types";
import { RowType } from "PFApp/booking/types/row";
import { useCallback } from "react";

import { useBookingRowsContext } from "../../context/booking_rows_context";
import { useBookingsOverviewContext } from "../../context/bookings_overview_context";

interface UseUpdateStateOfNewChildrenRowsUsingExistingRows {
  parentKey: string;
}

export const useUpdateChildrenStateUsingExistingRows = ({
  parentKey
}: UseUpdateStateOfNewChildrenRowsUsingExistingRows): ((
  newChildrenRows: Row[],
  prevChildrenRows: Row[]
) => void) => {
  const { calendarMode } = useBookingsOverviewContext();
  const { allBookingsModeRowsState, setAllBookingsModeRowsState, setExpandedRowsState } =
    useBookingRowsContext();
  const isAllBookingsModeEnabledForParent = !!allBookingsModeRowsState[parentKey];

  const getAllBookingsModeState = useCallback(
    (newChildrenRows: Row[], prevChildrenRows: Row[]): Record<string, boolean> => {
      const prevRowsByKey = keyBy(prevChildrenRows, "key");
      return newChildrenRows
        .filter(({ type }) => [RowType.Engagement, RowType.Role].includes(type))
        .reduce((result, current) => {
          const isChildrenRowNew = !prevRowsByKey[current.key];
          const isModeEnabledOnExistingChild = !!allBookingsModeRowsState[current.key];
          return {
            ...result,
            [current.key]:
              isModeEnabledOnExistingChild || (isChildrenRowNew && isAllBookingsModeEnabledForParent)
          };
        }, {} as Record<string, boolean>);
    },
    [allBookingsModeRowsState, isAllBookingsModeEnabledForParent]
  );

  return useCallback(
    (newChildrenRows, prevChildrenRows) => {
      let newExpandedRowsState = {};
      if (calendarMode === CalendarMode.Projects) {
        const newAllBookingsModeRowsState = getAllBookingsModeState(newChildrenRows, prevChildrenRows);
        setAllBookingsModeRowsState((prevState) => ({
          ...prevState,
          ...newAllBookingsModeRowsState
        }));
        newExpandedRowsState = pickBy(newAllBookingsModeRowsState, (state) => state);
      }
      setExpandedRowsState((prevState) => ({
        ...prevState,
        [calendarMode]: {
          ...prevState[calendarMode],
          ...newExpandedRowsState
        }
      }));
    },
    [getAllBookingsModeState, calendarMode, setAllBookingsModeRowsState]
  );
};
